import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, split } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createUploadLink } from 'apollo-upload-client'
import { uncrunch } from 'graphql-crunch'
import { createClient } from 'graphql-ws'
import { setContext } from '@apollo/client/link/context'

import { BACKEND_URL, WS_BACKEND_URL } from '../../variables'
import { fetchMorePolicy, replacePolicy } from '@dentolize/clinic-mobile/src/shared/utils/helpers'

const hasFile = operation => !operation.query.definitions.find(it => it.operation === 'mutation')

const httpLink = new HttpLink({
  uri: BACKEND_URL,
  credentials: 'include'
})

const uploadLink = createUploadLink({
  uri: BACKEND_URL,
  credentials: 'include',
  headers: {
    'X-Apollo-Operation-Name': 'Upload'
  }
})

// One id for this browser, shared by its tabs, so a notification caused by what was done here is not shown here too
const BROWSER_ID_KEY = 'browserId'
export const browserId = (() => {
  try {
    let id = localStorage.getItem(BROWSER_ID_KEY)
    if (!id) {
      id = `b-${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`
      localStorage.setItem(BROWSER_ID_KEY, id)
    }
    return id
  } catch (e) {
    return null
  }
})()

// A websocket cannot send the header the requests do, so the browser's id goes with the connection instead
const wsLink = new GraphQLWsLink(
  createClient({
    url: WS_BACKEND_URL,
    connectionParams: browserId ? { browserId } : {}
  })
)

const authLink = setContext((_, { headers }) => {
  // Check in backend that the using doing the mutation, or query is the same as the one logged in
  // To prevent user switching tabs and logs in with another user and then update something by mistake
  const userId = sessionStorage.getItem('userId')

  return {
    headers: {
      ...headers,
      'x-user-id': userId,
      ...(browserId ? { 'x-browser-id': browserId } : {})
    }
  }
})

export const SESSION_ACTIVITY_KEY = 'sessionLastActiveAt'

// The user acting is what keeps a session alive: their clicks, keys, touches and scrolls, and what they send (a
// mutation), but not what the app fetches by itself (refetches after live updates), which would keep an unattended
// screen signed in. The time is shared by the app's tabs, for each to lock itself when the session times out (see
// IdleSessionGuard), and sent with every request so the server times the session from it too.
const markActivity = () => {
  try {
    localStorage.setItem(SESSION_ACTIVITY_KEY, String(Date.now()))
  } catch (e) {}
}

let lastMarked = 0
if (typeof window !== 'undefined') {
  const onInteraction = () => {
    // at most every few seconds, so moving the mouse does not write on every event
    if (Date.now() - lastMarked < 5000) return
    lastMarked = Date.now()
    markActivity()
  }
  ;['mousedown', 'keydown', 'touchstart', 'wheel'].forEach(name =>
    window.addEventListener(name, onInteraction, { capture: true, passive: true })
  )
}

const lastUserActivity = () => {
  try {
    return Number(localStorage.getItem(SESSION_ACTIVITY_KEY)) || null
  } catch (e) {
    return null
  }
}

const activityLink = new ApolloLink((operation, forward) => {
  const definition = getMainDefinition(operation.query)
  // Logging out is not the user acting: counted, it would keep the app's other tabs from locking
  if (operation.operationName !== 'LOGOUT' && definition.kind === 'OperationDefinition' && definition.operation === 'mutation') {
    markActivity()
  }
  // Sent as how long ago, not when: both are read off this device's clock, so one set wrong does not change it
  const activeAt = lastUserActivity()
  if (activeAt) {
    const idleMs = Math.max(0, Date.now() - activeAt)
    operation.setContext(({ headers }) => ({ headers: { ...headers, 'x-user-idle-ms': String(idleMs) } }))
  }
  return forward(operation)
})

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
  },
  wsLink,
  httpLink
)

const mediatorLink = ApolloLink.split(hasFile, splitLink, uploadLink)

const uncruncher = new ApolloLink((operation, forward) =>
  forward(operation).map(response => {
    const definition = getMainDefinition(operation.query)
    if (definition.kind === 'OperationDefinition' && definition.operation === 'subscription') {
      return response
    }

    if (response.data) {
      response.data = uncrunch(response.data)
    }
    return response
  })
)

let lastTwoFactorRefetch = 0

const errorLink = onError(({ graphQLErrors, operation }) => {
  // The company now forces 2FA on a user without it, or the user must change their password: reload them so the app
  // asks them to
  if (
    graphQLErrors?.some(e => e.message === '2FA required' || e.message === 'Password reset required') &&
    Date.now() - lastTwoFactorRefetch > 10000
  ) {
    lastTwoFactorRefetch = Date.now()
    client.refetchQueries({ include: ['CURRENT_USER_QUERY'] })
  }

  if (
    operation &&
    (['GET_NOTIFICATIONS', 'GET_CALENDAR_QUERY', 'DASHBOARD_HOME_INFO', 'APPOINTMENT_WAITING_INFO'].includes(
      operation.operationName
    ) ||
      operation.variables.forDashboard)
  ) {
    return
  }

  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) =>
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    )
  }
})

const link = ApolloLink.from([errorLink, uncruncher, authLink, activityLink, mediatorLink])

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        patientApp: {
          merge: true
        },
        userDetails: (_, { args, toReference }) => toReference({ __typename: 'User', id: args.user }),
        groupDetails: (_, { args, toReference }) => toReference({ __typename: 'Group', id: args.group }),
        medicationDetails: (_, { args, toReference }) => toReference({ __typename: 'Medication', id: args.medication }),
        messageDetails: (_, { args, toReference }) => toReference({ __typename: 'Message', id: args.message }),
        ticketDetails: (_, { args, toReference }) => toReference({ __typename: 'Ticket', id: args.ticket }),
        monthlyExpenseDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'MonthlyExpense',
            id: args.monthlyExpense
          }),
        expenseDetails: (_, { args, toReference }) => toReference({ __typename: 'Expense', id: args.expense }),
        companyDetails: (_, { args, toReference }) => toReference({ __typename: 'Company', id: args.company }),
        referralDetails: (_, { args, toReference }) => toReference({ __typename: 'Referral', id: args.referral }),
        activeReminderDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'ReminderInstance',
            id: args.reminderInstance
          }),
        labOrderDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'LabOrder',
            id: args.labOrder
          }),
        labItemDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'LabItem',
            id: args.labItem
          }),
        attendanceDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'Attendance',
            id: args.attendance
          }),
        userSessionDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'UserSession',
            id: args.session
          }),
        userSecurityLogDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'UserSecurityLog',
            id: args.securityLog
          }),
        prescriptionDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'Prescription',
            id: args.prescription
          }),
        operationDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'Operation',
            id: args.operation
          }),
        treasuryDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'Treasury',
            id: args.treasury
          }),
        patientLinkDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'PatientLink',
            id: args.patientLink
          }),
        taskDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'Task',
            id: args.task
          }),
        taskInstanceDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'TaskInstance',
            id: args.taskInstance
          }),
        onlineConversationDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'OnlineConversation',
            id: args.conversation
          }),
        systemChatDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'SystemChat',
            id: args.systemChat
          }),
        leadViewDetails: (_, { args, toReference }) =>
          toReference({
            __typename: 'LeadView',
            id: args.leadView
          }),
        userActionsForTimeline: fetchMorePolicy,
        onlineConversations: fetchMorePolicy,
        conversationMessages: fetchMorePolicy,
        marketplaceItems: fetchMorePolicy,
        systemChats: fetchMorePolicy,
        systemChatMessages: replacePolicy,
        leadTimeline: fetchMorePolicy,
        patientLeadNotes: fetchMorePolicy
      }
    },
    SystemChat: {
      fields: {
        notifications: replacePolicy,
        chats: replacePolicy
      }
    },
    LeadStage: {
      fields: {
        leads: replacePolicy
      }
    },
    Procedure: {
      fields: {
        prices: fetchMorePolicy,
        doctorPercent: fetchMorePolicy
      }
    },
    ProcedureStep: {
      fields: {
        doctorPercent: fetchMorePolicy
      }
    }
  }
})

const client = new ApolloClient({
  link,
  cache,
  name: 'clinic:web',
  version: '1.0.1'
})

export default client
